/**
 * offlineCache — lectura sin conexión del OS (PWA).
 *
 * Guarda en IndexedDB la última respuesta buena de los GET «de lectura» más usados (Mi Día, tareas, coberturas,
 * agenda, notificaciones…). Si después no hay red, `api.request` devuelve lo último guardado marcado con
 * `_offline: true`, así la app se puede abrir y consultar en el campo sin señal. NO guarda escrituras:
 * sin conexión no se edita (el aviso lo dice).
 *
 * Seguridad: cada entrada va con el uid de quien la guardó (otra persona en el mismo navegador no la ve) y todo
 * se borra al cerrar sesión (`limpiarOffline`). Tope de tamaño por entrada y de cantidad; vence a los 7 días.
 */
const DB = "crew-offline";
const STORE = "gets";
const MAX_ENTRADAS = 80;
const MAX_BYTES = 1_500_000;
const VIDA_MS = 7 * 24 * 60 * 60 * 1000;

// Solo lectura de datos propios del OS. Nada de /api/auth, /api/portal, /api/push, /api/acceso, etc.
const PREFIJOS = [
  "/api/mi-dia", "/api/tareas", "/api/coberturas", "/api/clientes", "/api/notifications", "/api/equipo",
  "/api/grupos", "/api/triage", "/api/cockpit", "/api/agenda", "/api/pedidos-cliente", "/api/pedidos-agencia",
];

export const esCacheable = (path) => PREFIJOS.some(p => path === p || path.startsWith(p + "/") || path.startsWith(p + "?"));

function abrir() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return reject(new Error("sin IndexedDB"));
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => { r.result.createObjectStore(STORE, { keyPath: "k" }); };
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}
const tx = (db, modo) => db.transaction(STORE, modo).objectStore(STORE);
const esperar = (req) => new Promise((res, rej) => { req.onsuccess = () => res(req.result); req.onerror = () => rej(req.error); });

export async function guardarOffline(uid, path, data) {
  try {
    if (!uid || !esCacheable(path)) return;
    const texto = JSON.stringify(data);
    if (texto.length > MAX_BYTES) return;
    const db = await abrir();
    await esperar(tx(db, "readwrite").put({ k: `${uid}|${path}`, uid, path, ts: Date.now(), texto }));
    const todas = await esperar(tx(db, "readonly").getAll());
    if (todas.length > MAX_ENTRADAS) {
      const sobran = todas.sort((a, b) => a.ts - b.ts).slice(0, todas.length - MAX_ENTRADAS);
      const w = tx(db, "readwrite");
      sobran.forEach(e => w.delete(e.k));
    }
    db.close();
  } catch { /* sin almacenamiento: la app sigue igual */ }
}

export async function leerOffline(uid, path) {
  try {
    if (!uid || !esCacheable(path)) return null;
    const db = await abrir();
    const e = await esperar(tx(db, "readonly").get(`${uid}|${path}`));
    db.close();
    if (!e || Date.now() - e.ts > VIDA_MS) return null;
    return { data: JSON.parse(e.texto), ts: e.ts };
  } catch { return null; }
}

export async function limpiarOffline() {
  try { const db = await abrir(); await esperar(tx(db, "readwrite").clear()); db.close(); } catch { /* nada que limpiar */ }
}
